import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import PropTypes from "prop-types";

const GoogleTranslateContext = createContext();

export const useGoogleTranslateContext = () =>
    useContext(GoogleTranslateContext);

export const GoogleTranslateProvider = ({ children }) => {
    const initialized = useRef(false);

    const DEFAULT_LANGUAGE = "it";

    const getLangFromCookie = () => {
        const match = document.cookie.match(
            /(?:^|;\s*)googtrans=\/[^/]+\/([^;]+)/
        );

        return match?.[1] || DEFAULT_LANGUAGE;
    };

    const [currentLang, setCurrentLang] = useState(() =>
        getLangFromCookie()
    );

    const setLanguageCookie = (lang) => {
        document.cookie = `googtrans=/en/${lang};path=/;SameSite=Lax`;
    };

    const applyLanguage = (lang) => {
        if (lang === currentLang) return;

        setLanguageCookie(lang);

        const changeLanguage = () => {
            const select = document.querySelector(".goog-te-combo");

            if (!select) {
                return false;
            }

            select.value = lang;

            select.dispatchEvent(
                new Event("change", {
                    bubbles: true,
                })
            );

            setCurrentLang(lang);

            setTimeout(() => {
                window.location.reload();
            }, 500);

            return true;
        };

        if (changeLanguage()) return;

        let retry = 0;

        const interval = setInterval(() => {
            retry++;

            if (changeLanguage() || retry >= 20) {
                clearInterval(interval);
            }
        }, 500);
    };

    useEffect(() => {
        if (initialized.current) {
            return;
        }

        initialized.current = true;

        const savedLanguage = getLangFromCookie();

        // Set Italian only when no Google Translate cookie exists
        if (!document.cookie.includes("googtrans=")) {
            setLanguageCookie(DEFAULT_LANGUAGE);
        }

        let container = document.getElementById(
            "google_translate_element"
        );

        if (!container) {
            container = document.createElement("div");
            container.id = "google_translate_element";
            container.style.position = "absolute";
            container.style.left = "-9999px";
            container.style.top = "0";

            document.body.appendChild(container);
        }

        window.googleTranslateElementInit = () => {
            try {
                new window.google.translate.TranslateElement(
                    {
                        pageLanguage: "en",
                        includedLanguages:
                            "en,it,tr,pl,de,ru,nl,sv,da,zh-TW,ar",
                        layout:
                            window.google.translate.TranslateElement
                                .InlineLayout.VERTICAL,
                        autoDisplay: false,
                    },
                    "google_translate_element"
                );

                setTimeout(() => {
                    applyLanguage(savedLanguage);
                }, 1000);
            } catch (error) {
                console.error(error);
            }
        };

        const existing = document.querySelector(
            'script[src*="translate_a/element.js"]'
        );

        if (!existing) {
            const script = document.createElement("script");

            script.src =
                "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";

            script.async = true;
            script.defer = true;

            document.body.appendChild(script);
        } else {
            window.googleTranslateElementInit();
        }
    }, [applyLanguage]);

    const value = useMemo(
        () => ({
            currentLang,
            changeLanguage: applyLanguage,
        }),
        [applyLanguage, currentLang]
    );

    return (
        <GoogleTranslateContext.Provider value={value}>
            {children}
        </GoogleTranslateContext.Provider>
    );
};

GoogleTranslateProvider.propTypes = {
    children: PropTypes.node,
};